/**
 * Plan-entitlement layer (iter-2026).
 *
 * Single source of truth the UI trusts — mirrors the SAME data the backend
 * enforces via `GET /api/billing/usage-summary` (features + limits + usage +
 * connector allow-list). Never hardcodes plan state.
 *
 * Resolution order (must stay consistent everywhere):
 *   Plan entitlement → RBAC permission → resource limit → UI state
 *
 * - `entitled(feature)`   → plan feature flag (default true when unknown, so a
 *                            brand-new key never silently disables a feature).
 * - `limitReached(key)`   → used >= max (max null = unlimited → false).
 * - `connectorAllowed(k)` → plan allow-list check.
 * - `canUse(feature,{rbac})` → composes plan ∧ RBAC (plan first, then RBAC).
 */
import React, { createContext, useContext, useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { UpgradePlanDialog } from "@/components/billing/UpgradePlanDialog";
import { toast } from "sonner";

const Ctx = createContext(null);

export function EntitlementsProvider({ children }) {
    const { user } = useAuth();
    const [snap, setSnap] = useState(null);
    const [upgradeOpen, setUpgradeOpen] = useState(false);
    const load = useCallback(async () => {
        try {
            const r = await api.get("/billing/usage-summary");
            setSnap(r.data?.data || r.data || null);
        } catch { /* leave prior */ }
    }, []);
    useEffect(() => {
        load();
        const onRefresh = () => load();
        const onOpenUpgrade = () => openUpgradeInternal();
        window.addEventListener("billing:credits-updated", onRefresh);
        window.addEventListener("entitlements:refresh", onRefresh);
        window.addEventListener("entitlements:open-upgrade", onOpenUpgrade);
        return () => {
            window.removeEventListener("billing:credits-updated", onRefresh);
            window.removeEventListener("entitlements:refresh", onRefresh);
            window.removeEventListener("entitlements:open-upgrade", onOpenUpgrade);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [load]);

    // The upgrade→mail popup is SA/OA only (same as connector purchase flow).
    // Everyone else gets a "ask your admin" nudge.
    const openUpgradeInternal = useCallback(() => {
        const role = user?.role;
        if (role === "super_admin" || role === "org_admin") setUpgradeOpen(true);
        else toast.info("Needs plan to be upgraded. Please contact your org admin for the same.");
    }, [user?.role]);

    const planName = snap?.plan_slug
        ? snap.plan_slug.charAt(0).toUpperCase() + snap.plan_slug.slice(1)
        : "Standard";

    return (
        <Ctx.Provider value={{ snap, reload: load, openUpgrade: openUpgradeInternal, role: user?.role }}>
            {children}
            <UpgradePlanDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} currentPlanName={planName} />
        </Ctx.Provider>
    );
}

export function useEntitlements() {
    const ctx = useContext(Ctx);
    const snap = ctx?.snap || null;
    const features = snap?.features || {};
    const usage = snap?.usage || {};
    const access = snap?.access || {};

    const entitled = useCallback((feature) => {
        if (!feature) return true;
        if (!snap) return true;                 // pre-load: don't flash-lock
        const v = features[feature];
        return v === undefined ? true : !!v;    // unknown key = allowed
    }, [snap, features]);

    const limitReached = useCallback((limitKey) => {
        const u = usage[limitKey];
        if (!u) return false;
        if (u.max === null || u.max === undefined) return false;  // unlimited
        return (u.used ?? 0) >= u.max;
    }, [usage]);

    const usageOf = useCallback((limitKey) => usage[limitKey] || null, [usage]);

    const connectorAllowed = useCallback((key) => {
        const a = access?.allowed_connector_keys;
        if (!a || !a.mode) return true;
        const vals = a.values || [];
        if (a.mode === "allow_only") return vals.includes(key);
        if (a.mode === "allow_all_except") return !vals.includes(key);
        if (a.mode === "deny_all") return false;
        return true;
    }, [access]);

    // Plan ∧ RBAC. rbac defaults true so callers can pass canAccess(module).
    const canUse = useCallback((feature, { rbac = true } = {}) => entitled(feature) && !!rbac,
        [entitled]);

    return useMemo(() => ({
        ready: !!snap, planSlug: snap?.plan_slug || null,
        entitled, limitReached, usageOf, connectorAllowed, canUse,
        reload: ctx?.reload, openUpgrade: ctx?.openUpgrade || (() => {}), role: ctx?.role,
    }), [snap, entitled, limitReached, usageOf, connectorAllowed, canUse, ctx]);
}
